import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// Stand-in for the Stripe-hosted checkout page while payments are mocked.
// Deliberately styled like a hosted payment page (prefilled untouchable test
// card, TEST MODE badge) so the customer flow — redirect out, pay, redirect
// back — is identical to what real Stripe will do. Pay/Cancel round-trip
// through the backend, which runs the same webhook event processor as
// production would.
export function MockCheckoutPage() {
  useDocumentTitle("Checkout");
  const { sessionId } = useParams({ strict: false }) as { sessionId?: string };

  const session = useQuery({
    queryKey: ["mock-checkout-session", sessionId],
    queryFn: () => api.billing.mockSession(sessionId!),
    enabled: Boolean(sessionId),
  });

  const pay = useMutation({
    mutationFn: () => api.billing.mockPay(sessionId!),
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  });
  const cancel = useMutation({
    mutationFn: () => api.billing.mockCancel(sessionId!),
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  });

  if (session.isLoading) return <LoadingIndicator />;
  if (session.isError || !session.data) {
    return <ErrorComponent error={session.error ?? "Checkout session not found"} />;
  }

  const s = session.data;
  const isSetup = s.kind === "setup";
  const busy = pay.isPending || cancel.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div className="w-full max-w-md space-y-6 rounded-xl border bg-background p-8 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Lock className="size-4" />
            Secure checkout
          </div>
          <Badge variant="outline" className="border-amber-500/60 text-amber-600">
            TEST MODE
          </Badge>
        </div>

        <div>
          <div className="text-sm text-muted-foreground">
            {isSetup ? "Save a payment method" : "Add Boo credits"}
          </div>
          <div className="text-3xl font-semibold tracking-tight">
            {isSetup ? "Card on file" : formatMoney(s.amountUsd ?? 0)}
          </div>
        </div>

        {s.status !== "pending" ? (
          <p className="text-sm text-muted-foreground">
            This checkout session is {s.status}. You can close this page.
          </p>
        ) : (
          <>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Card number</Label>
                <Input value="4242 4242 4242 4242" disabled />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Expiry</Label>
                  <Input value="12 / 34" disabled />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">CVC</Label>
                  <Input value="123" disabled />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Payments are simulated until card processing goes live — no real
                charge happens.
              </p>
            </div>

            {(pay.isError || cancel.isError) && (
              <InlineError error={pay.error ?? cancel.error ?? "Something went wrong"} />
            )}

            <div className="space-y-2">
              <Button className="w-full" onClick={() => pay.mutate()} disabled={busy}>
                {pay.isPending
                  ? "Processing…"
                  : isSetup
                    ? "Save card"
                    : `Pay ${formatMoney(s.amountUsd ?? 0)}`}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => cancel.mutate()}
                disabled={busy}
              >
                Cancel and go back
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
